'use client';

import { FC, useState } from 'react';

import { Button, Modal, RadioButtonGroup } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { NAVIGATION_MODAL_ZINDEX } from '@telialt/common-constants/src/modal';

import { PLAN_ORDER_OPTION, PLAN_ORDERING_TEST_ID } from '../constants';
import { PlanOrderOption, ServiceType } from '../model';
import { usePlanOrderOptionSelectModalControls } from './usePlanOrderOptionSelectModalControls';

type Props = {
    serviceType: ServiceType;
    onSelect?: (orderOption: PlanOrderOption) => void;
    onCancel?: () => void;
};
export const PlanOrderOptionSelectModal: FC<Props> = ({ serviceType, onSelect, onCancel }) => {
    const [selectedOption, setSelectedOption] = useState<PlanOrderOption>(PLAN_ORDER_OPTION.PROLONGATION);
    const { t } = useTranslations();
    const { isPlanOrderSelectModalOpen, closePlanOrderOptionSelectModal, editPlanOrderOptionSelectModal } =
        usePlanOrderOptionSelectModalControls();

    const onPlanOrderSelect = (value: string) => setSelectedOption(value as PlanOrderOption);

    const onContinue = () => {
        editPlanOrderOptionSelectModal(false);
        onSelect(selectedOption);
    };

    const title = {
        MHS: t['plans.plan_order_select_modal.title'],
        MBB: t['plans.mbb.plan_order_select_modal.title'],
    }[serviceType];

    const newContractOptionMessage = {
        MHS: t['plans.plan_order_select_modal.new_contract_radio'],
        MBB: t['plans.mbb.plan_order_select_modal.new_contract_radio'],
    }[serviceType];

    return (
        <Modal
            open={isPlanOrderSelectModalOpen}
            onOpenChange={(open) => {
                if (!open) {
                    closePlanOrderOptionSelectModal();
                    onCancel?.();
                }
            }}
        >
            <Modal.Content
                title={title}
                closeButtonAriaLabel={t['common.button.close']}
                showCloseButton={true}
                zIndex={NAVIGATION_MODAL_ZINDEX}
                data-test={PLAN_ORDERING_TEST_ID.ORDER_SELECTION_MODAL}
                actions={
                    <>
                        <Button
                            variant="primary"
                            onClick={onContinue}
                            data-test={PLAN_ORDERING_TEST_ID.ORDER_MODAL_CONTINUE}
                        >
                            {t['plans.plan_order_select_modal.action_continue']}
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                closePlanOrderOptionSelectModal();
                                onCancel?.();
                            }}
                            data-test={PLAN_ORDERING_TEST_ID.ORDER_MODAL_CANCEL}
                        >
                            {t['plans.plan_order_select_modal.action_cancel']}
                        </Button>
                    </>
                }
            >
                <RadioButtonGroup
                    id={PLAN_ORDERING_TEST_ID.ORDER_RADIO_GROUP}
                    data-test={PLAN_ORDERING_TEST_ID.ORDER_RADIO_GROUP}
                    heading={t['plans.plan_order_select_modal.radio_title']}
                    items={[
                        {
                            id: 'plan-order-radio-group-prolongation',
                            value: PLAN_ORDER_OPTION.PROLONGATION,
                            label: t['plans.plan_order_select_modal.prolongation_radio'],
                        },
                        {
                            id: 'plan-order-radio-group-new-contract',
                            value: PLAN_ORDER_OPTION.NEW_CONTRACT,
                            label: newContractOptionMessage,
                        },
                    ]}
                    required={true}
                    onValueChange={onPlanOrderSelect}
                    value={selectedOption}
                />
            </Modal.Content>
        </Modal>
    );
};
